/**
 * Numbering follows the boxes on the ICS 215-CG form, as printed by
 * `services/coms/src/forms/ICS_215_CG_Operational_Planning_Worksheet.docx`.
 *
 * Note the sheet numbers each resource total separately — 12 required, 13 on
 * hand, 14 needed — which is why Prepared By is box 15 rather than 13. Some
 * printings of the worksheet combine the three into a single "Resource Totals"
 * box and end at 13; the template in this repo is the one we export, so the
 * card follows it.
 *
 * Boxes 1 to 4 are read-only, filled from the incident and its operational
 * period. Boxes 5 to 11 are the columns of the assignment grid.
 */

export const ICS215_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  datePrepared: '3. Date / Time Prepared:',
  operationalPeriod: '4. Operational Period (Date/Time):',
  divisionGroup: '5. Division / Group / Other Location',
  workAssignments: '6. Work Assignments',
  kindsOfResources: '7. Kinds of Resources',
  overheadPositions: '8. Overhead Positions',
  specialEquipment: '9. Special Equipment & Supplies',
  reportingLocation: '10. Reporting Location',
  requestedArrivalTime: '11. Requested Arrival Time',
  totalResourcesRequired: '12. Total Resources Required',
  totalResourcesOnHand: '13. Total Resources On Hand',
  totalResourcesNeeded: '14. Total Resources Needed',
  preparedBy: '15. Prepared By (Name / Position):',
} as const;

/** The grid boxes, in the order the sheet prints them left to right. */
export const ICS215_ASSIGNMENT_GRID_LABEL = `${ICS215_BOX_LABELS.divisionGroup} — ${ICS215_BOX_LABELS.requestedArrivalTime}`;

/**
 * The four sub-columns box 7 prints under each kind of resource.
 *
 * "Have" is what is already on scene and "Need" is the shortfall, so only
 * Required and Have are typed; Need is the difference.
 */
export const ICS215_RESOURCE_COLUMNS = [
  { label: 'Required', key: 'required' },
  { label: 'Have', key: 'have' },
  { label: 'Need', key: 'need' },
] as const;

export const ICS215_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

export const ICS215_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const ICS215_DATE_PREPARED_HINT =
  'This box is read-only. A signed worksheet prints when it was signed; an unsigned one prints when it is exported.';

export const ICS215_OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

export const ICS215_ASSIGNMENT_GRID_HINT =
  'Each row is a tactic and its work assignment. Resource counts feed the totals in boxes 12 to 14, and Need is calculated as Required minus Have.';

export const ICS215_PREPARED_BY_HINT =
  'Signing freezes a snapshot of the whole worksheet, so the exported form keeps the grid exactly as it was signed.';
